'use client';
import React, { useState } from 'react';
import { mockStores } from '@/lib/mockData';
import AppImage from '@/components/ui/AppImage';
import StarRating from '@/components/ui/StarRating';
import TrustScoreBar from '@/components/ui/TrustScoreBar';
import {
  CheckCircle2, XCircle, ShieldCheck, Star, MapPin,
  Building2, Receipt, Users, Package, BadgeCheck, FileText, Calendar
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const AUTH_PARAM_LABELS = [
  { key: 'businessRegistration', label: 'Business Registration Verified', icon: Building2 },
  { key: 'gstVerified', label: 'GST Verified', icon: Receipt },
  { key: 'ownerIdentity', label: 'Owner Identity Verified', icon: Users },
  { key: 'physicalStore', label: 'Physical Store Verified', icon: MapPin },
  { key: 'productAuthenticity', label: 'Product Authenticity Verified', icon: Package },
  { key: 'brandAuthorization', label: 'Brand Authorization Verified', icon: BadgeCheck },
  { key: 'invoiceVerification', label: 'Invoice Verification', icon: FileText },
  { key: 'customerSatisfaction', label: 'Customer Satisfaction Verified', icon: Star },
];

const TABS = [
  { id: 'tab-overview', label: 'Overview' },
  { id: 'tab-authenticity', label: 'Authenticity Shield' },
  { id: 'tab-reviews', label: 'Customer Reviews' },
  { id: 'tab-gallery', label: 'Gallery' },
];

export default function StoreDetailBody() {
  const store = mockStores[0];
  const [activeTab, setActiveTab] = useState('tab-overview');

  const verifiedCount = AUTH_PARAM_LABELS.filter(
    (p) => store.authParams[p.key as keyof typeof store.authParams] === true
  ).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-8">
      {/* Main content */}
      <div className="lg:col-span-2 xl:col-span-3">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-border mb-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-all duration-150 -mb-px
                ${activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {activeTab === 'tab-overview' && (
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">About This Store</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">{store.description}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Years in Business', value: `${store.authParams.yearsInBusiness}+ years` },
                { label: 'Trust Score', value: `${store.trustScore}/100` },
                { label: 'Avg Rating', value: `${store.averageRating}/5.0` },
                { label: 'Parameters Met', value: `${verifiedCount}/8` },
              ].map((stat) => (
                <div key={`stat-${stat.label}`} className="bg-muted rounded-xl p-4 text-center">
                  <p className="text-xl font-bold text-primary font-tabular">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Store Location</h3>
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-foreground">{store.address}</p>
                  <p className="text-sm text-muted-foreground">{store.city}, {store.state}</p>
                  <a
                    href={store.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-primary hover:underline"
                  >
                    Open in Google Maps →
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Authenticity Shield tab */}
        {activeTab === 'tab-authenticity' && (
          <div>
            <div className="flex items-center gap-4 mb-6 p-5 bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl border border-primary/20">
              <div className="w-16 h-16 badge-verified rounded-2xl flex items-center justify-center shadow-shield flex-shrink-0">
                <ShieldCheck size={32} className="text-white shield-pulse" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Authenticity Shield Active</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {verifiedCount} of 8 parameters verified · Trust Score {store.trustScore}/100
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Last verified: {store.verificationDate}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">Verification Progress</span>
                <span className="text-sm font-bold text-primary font-tabular">{verifiedCount}/8 passed</span>
              </div>
              <TrustScoreBar score={(verifiedCount / 8) * 100} height={8} showLabel={false} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {AUTH_PARAM_LABELS.map((param) => {
                const passed = store.authParams[param.key as keyof typeof store.authParams] === true;
                const Icon = param.icon;
                return (
                  <div
                    key={`param-${param.key}`}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all duration-150
                      ${passed
                        ? 'bg-green-50 border-green-200 text-foreground'
                        : 'bg-red-50 border-red-200 text-muted-foreground'
                      }`}
                  >
                    <Icon size={16} className={passed ? 'text-primary' : 'text-destructive'} />
                    <span className="flex-1 text-sm font-medium">{param.label}</span>
                    {passed
                      ? <CheckCircle2 size={18} className="text-primary flex-shrink-0" />
                      : <XCircle size={18} className="text-destructive flex-shrink-0" />
                    }
                  </div>
                );
              })}
            </div>

            {store.authParams.verificationNotes && (
              <div className="bg-muted rounded-xl p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={14} className="text-muted-foreground" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Verification Notes</span>
                </div>
                <p className="text-sm text-foreground leading-relaxed">{store.authParams.verificationNotes}</p>
              </div>
            )}
          </div>
        )}

        {/* Reviews tab */}
        {activeTab === 'tab-reviews' && (
          <div>
            <div className="flex items-center gap-4 mb-6 p-5 bg-muted rounded-2xl">
              <div className="text-center">
                <p className="text-4xl font-bold text-foreground font-tabular">{store.averageRating}</p>
                <StarRating rating={store.averageRating} size={18} />
                <p className="text-xs text-muted-foreground mt-1">{store.reviews.length} reviews</p>
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = store.reviews.filter((r) => Math.round(r.rating) === star).length;
                  const pct = store.reviews.length > 0 ? (count / store.reviews.length) * 100 : 0;
                  return (
                    <div key={`star-bar-${star}`} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground w-4 text-right font-tabular">{star}</span>
                      <Star size={11} className="star-filled flex-shrink-0" fill="currentColor" />
                      <div className="flex-1 bg-secondary rounded-full h-2 overflow-hidden">
                        <div className="bg-trust-gold h-2 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-muted-foreground w-4 font-tabular">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-4">
              {store.reviews.map((review) => (
                <div key={review.id} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {review.avatar}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-foreground">{review.reviewerName}</span>
                        <span className="text-xs text-muted-foreground">{review.reviewDate}</span>
                      </div>
                      <StarRating rating={review.rating} size={13} />
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{review.review}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gallery tab */}
        {activeTab === 'tab-gallery' && (
          <div>
            {store.gallery.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {store.gallery.map((img, idx) => (
                  <div key={`gallery-${idx}`} className="rounded-2xl overflow-hidden border border-border h-48">
                    <AppImage
                      src={img}
                      alt={`${store.name} store interior gallery photo ${idx + 1}`}
                      width={400}
                      height={250}
                      className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-sm">No gallery images available for this store.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="lg:col-span-1 flex flex-col gap-4">
        {/* Trust Score Ring */}
        <div className="bg-card border border-border rounded-2xl p-5 text-center">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Trust Score</p>
          <TrustScoreRing score={mockStores[0].trustScore} />
          <p className="text-xs text-muted-foreground mt-3">
            Based on {verifiedCount}/8 verified parameters
          </p>
        </div>

        {/* Quick Info */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Quick Info</h3>
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Category</span>
              <span className="font-medium text-foreground">{store.category}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">City</span>
              <span className="font-medium text-foreground">{store.city}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">State</span>
              <span className="font-medium text-foreground">{store.state}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Est.</span>
              <span className="font-medium text-foreground">{store.authParams.yearsInBusiness}+ yrs ago</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Verified On</span>
              <span className="font-medium text-foreground">{store.verificationDate}</span>
            </div>
          </div>
        </div>

        {/* Shield summary */}
        <div className="bg-gradient-to-br from-green-50 to-teal-50 border border-primary/20 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={18} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">Why Verified?</span>
          </div>
          <ul className="flex flex-col gap-2">
            {AUTH_PARAM_LABELS.filter((p) => store.authParams[p.key as keyof typeof store.authParams] === true).slice(0, 4).map((p) => (
              <li key={`why-${p.key}`} className="flex items-center gap-2 text-xs text-foreground">
                <CheckCircle2 size={13} className="text-primary flex-shrink-0" />
                {p.label}
              </li>
            ))}
          </ul>
          {verifiedCount > 4 && (
            <p className="text-xs text-muted-foreground mt-2">+{verifiedCount - 4} more verified parameters</p>
          )}
        </div>

        {/* Verification date */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={14} className="text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Verification Date</span>
          </div>
          <p className="text-lg font-bold text-foreground font-tabular">{store.verificationDate}</p>
          <p className="text-xs text-muted-foreground mt-1">Make me Authentic certification valid for 1 year</p>
        </div>
      </div>
    </div>
  );
}

function TrustScoreRing({ score }: { score: number }) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={128} height={128} className="-rotate-90">
        <circle cx={64} cy={64} r={radius} className="trust-ring-track" />
        <circle
          cx={64}
          cy={64}
          r={radius}
          className="trust-ring-fill"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-primary font-tabular">{score}</span>
        <span className="text-xs text-muted-foreground">/100</span>
      </div>
    </div>
  );
}